/**
 * AnalyticsDashboard — admin-only deep analytics tab.
 *
 * Renders 4 stat cards + 4 visualizations (daily line, monthly bar,
 * top salespeople bar, category share donut) plus a top-customers table.
 * Data comes from GET /api/admin/analytics — one round-trip.
 */
import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Skeleton } from '../ui/skeleton';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { api, formatINR, todayIst, addDaysIst, istWeekStart, istMonthStart } from '../../lib/store';
import {
  ResponsiveContainer,
  LineChart, Line,
  BarChart, Bar,
  PieChart, Pie, Cell,
  XAxis, YAxis, Tooltip, Legend,
  CartesianGrid,
} from 'recharts';
import { TrendingUp, FileText, IndianRupee, Truck, Users, Crown, Boxes, GitCompareArrows, ArrowUp, ArrowDown, Minus, Calendar, ChevronRight, ChevronDown } from 'lucide-react';
import AnimatedCategoryPie from './AnimatedCategoryPie';
import { C, AXIS_TICK, GRID, TOOLTIP_STYLE } from './chartTheme';

// Short rupee formatter for chart axes — shows ₹1.2L, ₹56K etc.
const short = (n) => {
  if (n === null || n === undefined || isNaN(n)) return '₹0';
  const a = Math.abs(n);
  if (a >= 10000000) return `₹${(n / 10000000).toFixed(1)}Cr`;
  if (a >= 100000) return `₹${(n / 100000).toFixed(1)}L`;
  if (a >= 1000) return `₹${(n / 1000).toFixed(0)}K`;
  return `₹${Math.round(n)}`;
};

const dayLabel = (iso) => {
  if (!iso) return '';
  const [, m, d] = iso.split('-');
  return `${d}/${m}`;
};

// Pretty "last quote X days ago" badge: shows colour + label based on staleness.
// 0d = green (today), 1-7d = green, 8-21d = amber, 22+ = red, no quotes = grey.
const lastQuoteBadge = (days) => {
  if (days === null || days === undefined) {
    return { label: 'No quotes yet', cls: 'bg-heda-cream text-heda-mute' };
  }
  let label;
  if (days === 0) label = 'Today';
  else if (days === 1) label = '1d ago';
  else label = `${days}d ago`;

  let cls;
  if (days <= 7) cls = 'bg-emerald-50 text-emerald-700';
  else if (days <= 21) cls = 'bg-amber-50 text-amber-700';
  else cls = 'bg-red-50 text-red-700';

  return { label, cls };
};

const monthLabel = (yyyymm) => {
  if (!yyyymm) return '';
  const [, m] = yyyymm.split('-');
  const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
  return months[parseInt(m, 10) - 1] || yyyymm;
};

// Full "Month YYYY" label for the comparison card selects
const monthFullLabel = (yyyymm) => {
  if (!yyyymm) return '';
  const [y, m] = yyyymm.split('-');
  const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
  return `${months[parseInt(m, 10) - 1] || m} ${y}`;
};

// Percent-change helper — returns `{ pct, dir, label }`.
// dir: 'up' / 'down' / 'flat'. label: "+12%", "-8%", "0%"
const pctChange = (curr, base) => {
  const c = Number(curr) || 0;
  const b = Number(base) || 0;
  if (b === 0) {
    if (c === 0) return { pct: 0, dir: 'flat', label: '0%' };
    return { pct: 100, dir: 'up', label: 'New' };
  }
  const pct = ((c - b) / b) * 100;
  const rounded = Math.abs(pct) < 10 ? pct.toFixed(1) : Math.round(pct);
  if (Math.abs(pct) < 0.05) return { pct: 0, dir: 'flat', label: '0%' };
  return { pct, dir: pct > 0 ? 'up' : 'down', label: `${pct > 0 ? '+' : ''}${rounded}%` };
};

function StatCard({ icon: Icon, label, value, sub, accent }) {
  return (
    <div className="heda-card p-4 sm:p-5" data-testid={`stat-${label.toLowerCase().replace(/\s+/g, '-')}`}>
      <div className="flex items-start justify-between gap-2">
        <span className="text-[11px] uppercase tracking-wider text-heda-mute">{label}</span>
        <span className={`flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-full border ${accent}`}>
          <Icon className="w-4 h-4" />
        </span>
      </div>
      <p className="mt-2 font-display text-2xl sm:text-3xl font-bold tabular-nums text-heda-ink truncate">{value}</p>
      {sub && <p className="mt-0.5 text-[11.5px] text-heda-mute">{sub}</p>}
    </div>
  );
}

// Date-range filter chips for the analytics header.
// Presets: All, This Week, This Month, Last 30d, Custom. When custom is
// active the two date inputs become editable so the admin can dial in
// any window (e.g. "2 June → 12 June").
export function DateRangeChips({ preset, startDate, endDate, onPreset, onStartDate, onEndDate }) {
  const PRESETS = [
    { key: 'all',    label: 'All Time' },
    { key: 'week',   label: 'This Week' },
    { key: 'month',  label: 'This Month' },
    { key: '30d',    label: 'Last 30d' },
    { key: 'custom', label: 'Custom' },
  ];
  const isCustom = preset === 'custom';
  return (
    <div
      className="flex flex-col sm:flex-row sm:items-center gap-2"
      data-testid="analytics-date-range"
    >
      <div className="flex max-w-full flex-wrap items-center gap-1 rounded-3xl border border-heda-line bg-white p-1 self-start sm:rounded-full">
        {PRESETS.map(p => (
          <button
            key={p.key}
            type="button"
            onClick={() => onPreset(p.key)}
            className={`shrink-0 whitespace-nowrap rounded-full px-3 py-1.5 text-[12.5px] font-semibold transition ${
              preset === p.key ? 'bg-heda-ink text-white' : 'text-heda-mute hover:text-heda-ink'
            }`}
            data-testid={`analytics-range-${p.key}`}
          >
            {p.label}
          </button>
        ))}
      </div>
      {(isCustom || (startDate && endDate)) && (
        <div className="flex items-center gap-1.5 text-xs sm:ml-auto">
          <Calendar className="w-4 h-4 text-heda-gold flex-shrink-0" />
          <input
            type="date"
            value={startDate}
            onChange={(e) => onStartDate(e.target.value)}
            className="h-9 min-w-0 max-w-[140px] rounded-xl border border-heda-line bg-white px-2 text-base sm:text-xs text-heda-ink focus:border-heda-gold focus:outline-none"
            data-testid="analytics-range-start"
          />
          <span className="text-heda-mute">→</span>
          <input
            type="date"
            value={endDate}
            onChange={(e) => onEndDate(e.target.value)}
            className="h-9 min-w-0 max-w-[140px] rounded-xl border border-heda-line bg-white px-2 text-base sm:text-xs text-heda-ink focus:border-heda-gold focus:outline-none"
            data-testid="analytics-range-end"
          />
        </div>
      )}
    </div>
  );
}

// Side-by-side comparison tile for the month-vs-month card.
// Renders: label, two big numbers (A vs B), and a delta chip with arrow.
function CompareTile({ label, valueA, valueB, cmp, testId }) {
  const ArrowIcon = cmp.dir === 'up' ? ArrowUp : cmp.dir === 'down' ? ArrowDown : Minus;
  const deltaColor =
    cmp.dir === 'up'   ? 'bg-emerald-50 text-emerald-700' :
    cmp.dir === 'down' ? 'bg-red-50 text-red-700' :
                         'bg-heda-cream text-heda-mute';
  return (
    <div className="rounded-xl border border-heda-line bg-heda-cream/40 p-4" data-testid={testId}>
      <p className="text-[10.5px] font-semibold uppercase tracking-wider text-heda-mute mb-2">{label}</p>
      <div className="flex items-end justify-between gap-2 mb-2">
        <div>
          <p className="text-[11px] text-heda-mute uppercase">A</p>
          <p className="font-display text-2xl font-bold tabular-nums text-heda-ink">{valueA}</p>
        </div>
        <div className="text-right">
          <p className="text-[11px] text-heda-mute uppercase">B</p>
          <p className="font-display text-lg font-semibold tabular-nums text-heda-sub">{valueB}</p>
        </div>
      </div>
      <div className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider ${deltaColor}`}>
        <ArrowIcon className="w-3 h-3" />
        {cmp.label}
      </div>
    </div>
  );
}

// =====================================================================
// SalespersonBreakdownTable — full breakdown with click-to-expand drill-down.
// Each row can be clicked to fetch that salesperson's recent quotations
// (scoped to the current date range) and show them inline. A "View all"
// link jumps to the Quotation History pre-filtered to the same salesperson
// and date range so admins land where they can act.
// =====================================================================
function SalespersonBreakdownTable({ rows, startDate, endDate }) {
  const navigate = useNavigate();
  const [expandedId, setExpandedId] = useState(null);
  const [quoteCache, setQuoteCache] = useState({}); // userId -> {loading, items, total, error}

  const loadQuotes = (userId) => {
    setQuoteCache(c => ({ ...c, [userId]: { loading: true, items: [], total: 0, error: '' } }));
    const params = { limit: 5, page: 1, salesperson_id: userId };
    if (startDate) params.start_date = startDate;
    if (endDate) params.end_date = endDate;
    api.getQuotations(params)
      .then(r => {
        setQuoteCache(c => ({ ...c, [userId]: {
          loading: false,
          items: r.data.quotations || [],
          total: r.data.total || 0,
          error: '',
        }}));
      })
      .catch(err => {
        setQuoteCache(c => ({ ...c, [userId]: {
          loading: false, items: [], total: 0,
          error: err.response?.data?.detail || 'Failed to load quotations',
        }}));
      });
  };

  const toggleRow = (userId) => {
    if (expandedId === userId) { setExpandedId(null); return; }
    setExpandedId(userId);
    if (!quoteCache[userId]) loadQuotes(userId);
  };

  const goToHistory = (s, e) => {
    // Uses location.state so QuotationsPage can seed its filter without a
    // reload. Falls back to no-op cleanly if navigation isn't allowed.
    e.stopPropagation();
    navigate('/quotations', {
      state: {
        salesperson_id: s.user_id,
        salesperson_name: s.name,
        start_date: startDate || '',
        end_date: endDate || '',
      }
    });
  };

  return (
    <Card className="rounded-2xl border-heda-line bg-white shadow-none" data-testid="table-salespeople">
      <CardHeader className="border-b border-heda-line px-5 py-3">
        <CardTitle className="flex items-center gap-2 font-display text-[15px] font-bold text-heda-ink">
          <Users className="w-4 h-4 text-heda-gold" /> Full Salesperson Breakdown
        </CardTitle>
        <p className="text-[11.5px] text-heda-mute">Click a row for their recent quotes</p>
      </CardHeader>
      <CardContent className="p-0">
        <div className="overflow-x-auto">
          <table className="w-full text-left text-sm">
            <thead className="heda-thead">
              <tr>
                <th className="w-6 pl-4 pr-0 py-2.5"></th>
                <th className="px-3 sm:px-4 py-2.5 font-semibold">Salesperson</th>
                <th className="px-3 sm:px-4 py-2.5 font-semibold hidden md:table-cell">Role</th>
                <th className="px-3 sm:px-4 py-2.5 font-semibold hidden sm:table-cell">Last Quote</th>
                <th className="px-3 sm:px-4 py-2.5 font-semibold text-right">Quotations</th>
                <th className="px-3 sm:px-4 py-2.5 font-semibold text-right">Total Value</th>
                <th className="px-3 sm:px-4 py-2.5 font-semibold text-right hidden md:table-cell">Avg / Quote</th>
              </tr>
            </thead>
            <tbody>
              {rows.length === 0 ? (
                <tr><td colSpan="7" className="px-5 py-10 text-center text-sm text-heda-mute">No salespeople yet</td></tr>
              ) : rows.map((s) => {
                const badge = lastQuoteBadge(s.days_since_last_quote);
                const isOpen = expandedId === s.user_id;
                const cached = quoteCache[s.user_id];
                return (
                  <React.Fragment key={s.user_id}>
                    <tr
                      className={`border-t border-heda-line cursor-pointer hover:bg-heda-cream/50 ${isOpen ? 'bg-heda-gold-tint/40' : ''}`}
                      onClick={() => toggleRow(s.user_id)}
                      data-testid={`salesperson-row-${s.user_id}`}
                    >
                      <td className="pl-4 pr-0 py-3 text-heda-mute">
                        {isOpen ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
                      </td>
                      <td className="px-3 sm:px-4 py-3">
                        <span className="font-medium text-heda-ink">{s.name}</span>
                        {s.staff_code && <span className="ml-2 rounded-full bg-heda-olive-tint px-2 py-0.5 font-mono text-[10px] font-bold text-heda-olive-dark">{s.staff_code}</span>}
                      </td>
                      <td className="px-3 sm:px-4 py-3 hidden md:table-cell"><span className="rounded-full bg-heda-cream px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider text-heda-mute">{s.role || '—'}</span></td>
                      <td className="px-3 sm:px-4 py-3 hidden sm:table-cell">
                        <span
                          className={`inline-flex items-center rounded-full px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider ${badge.cls}`}
                          title={s.last_quote_at ? new Date(s.last_quote_at).toLocaleString('en-IN', { dateStyle: 'medium', timeStyle: 'short' }) : 'No quotes yet'}
                          data-testid={`last-quote-${s.user_id}`}
                        >
                          {badge.label}
                        </span>
                      </td>
                      <td className="px-3 sm:px-4 py-3 text-right tabular-nums text-heda-ink/80">{s.count}</td>
                      <td className="px-3 sm:px-4 py-3 text-right tabular-nums font-bold text-heda-ink">{formatINR(s.value)}</td>
                      <td className="px-3 sm:px-4 py-3 text-right tabular-nums text-heda-sub hidden md:table-cell">{s.count > 0 ? formatINR(Math.round(s.value / s.count)) : '—'}</td>
                    </tr>
                    {isOpen && (
                      <tr className="bg-heda-gold-tint/40" data-testid={`salesperson-drilldown-${s.user_id}`}>
                        <td colSpan="7" className="px-3 sm:px-4 py-3">
                          <SalespersonDrilldownPanel
                            salesperson={s}
                            cache={cached}
                            onViewAll={(e) => goToHistory(s, e)}
                            statusCounts={{
                              closed: s.closed_count || 0,
                              pending: s.pending_count || 0,
                              lost: s.lost_count || 0,
                              closed_value: s.closed_value || 0,
                              lost_value: s.lost_value || 0,
                            }}
                          />
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                );
              })}
            </tbody>
          </table>
        </div>
      </CardContent>
    </Card>
  );
}

// Inline panel rendered under an expanded salesperson row. Shows:
//   • closed/pending/lost badges from the analytics payload
//   • top 5 recent quotations (call to /quotations?salesperson_id=…)
//   • "View all" button → navigates to Quotation History pre-filtered
function SalespersonDrilldownPanel({ salesperson, cache, onViewAll, statusCounts }) {
  const s = salesperson;
  const c = cache || { loading: true, items: [], total: 0 };
  return (
    <div className="space-y-3">
      {/* Status chips (closed/pending/lost) — from analytics payload */}
      <div className="flex flex-wrap gap-2 text-[11px]">
        <span className="inline-flex items-center gap-1 rounded-full bg-emerald-50 text-emerald-700 px-2 py-0.5">
          <span className="font-semibold">{statusCounts.closed}</span> closed · {formatINR(statusCounts.closed_value)}
        </span>
        <span className="inline-flex items-center gap-1 rounded-full bg-amber-50 text-amber-700 px-2 py-0.5">
          <span className="font-semibold">{statusCounts.pending}</span> pending
        </span>
        <span className="inline-flex items-center gap-1 rounded-full bg-red-50 text-red-700 px-2 py-0.5">
          <span className="font-semibold">{statusCounts.lost}</span> lost · {formatINR(statusCounts.lost_value)}
        </span>
      </div>

      {/* Recent quotations */}
      <div>
        <p className="heda-label">
          {c.loading ? 'Loading recent quotations…' : `Recent quotations (top ${c.items.length} of ${c.total})`}
        </p>
        {c.loading && <Skeleton className="h-20 w-full rounded-xl" />}
        {c.error && <p className="text-xs text-red-700">{c.error}</p>}
        {!c.loading && !c.error && c.items.length === 0 && (
          <p className="text-xs text-heda-mute italic py-3">No quotations by {s.name} in this range.</p>
        )}
        {!c.loading && !c.error && c.items.length > 0 && (
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
            {c.items.map(q => (
              <div
                key={q.id}
                className="rounded-xl border border-heda-line bg-white p-3 text-xs"
                data-testid={`salesperson-quote-${q.id}`}
              >
                <div className="flex items-center justify-between gap-2">
                  <span className="font-mono font-semibold text-heda-ink">{q.quotation_number || q.id?.slice(0, 8)}</span>
                  <StatusPill status={q.order_status} />
                </div>
                <div className="mt-1 flex items-center justify-between gap-2">
                  <span className="text-heda-ink truncate">{q.customer?.name || 'Unknown'}</span>
                  <span className="tabular-nums font-semibold text-heda-ink">{formatINR(q.grand_total)}</span>
                </div>
                <div className="text-[11px] text-heda-mute mt-0.5">
                  {q.created_at ? new Date(q.created_at).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' }) : '—'}
                  {q.customer?.mobile ? ` · ${q.customer.mobile}` : ''}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {c.total > 0 && (
        <button
          type="button"
          onClick={onViewAll}
          className="heda-pill"
          data-testid={`view-all-quotes-${s.user_id}`}
        >
          View all {c.total} quotations by {s.name.split(' ')[0]}
          <ChevronRight className="w-3.5 h-3.5" />
        </button>
      )}
    </div>
  );
}

function StatusPill({ status }) {
  const st = (status || 'pending').toLowerCase();
  const cls = st === 'closed' ? 'bg-emerald-50 text-emerald-700'
             : st === 'lost'   ? 'bg-red-50 text-red-700'
                               : 'bg-amber-50 text-amber-700';
  return (
    <span className={`inline-flex items-center rounded-full px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider ${cls}`}>
      {st}
    </span>
  );
}


export default function AnalyticsDashboard() {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  // Month-compare card state — populated once data lands.
  const [monthA, setMonthA] = useState('');
  const [monthB, setMonthB] = useState('');

  // Date range filter. Chips: 'all' (default), 'week', 'month',
  // '30d', 'custom'. When custom, the two inputs drive startDate/endDate.
  // Passed to /admin/analytics as IST YYYY-MM-DD strings. (A UTC
  // toISOString() date is still yesterday until 05:30 IST.)
  const isoDaysAgo = (n) => addDaysIst(-n);
  const isoToday = () => todayIst();
  const [rangePreset, setRangePreset] = useState('all');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');

  const applyPreset = (preset) => {
    setRangePreset(preset);
    if (preset === 'all') { setStartDate(''); setEndDate(''); }
    else if (preset === 'week') {
      // Monday-start IST week (matches Indian business convention).
      setStartDate(istWeekStart());
      setEndDate(isoToday());
    }
    else if (preset === 'month') {
      setStartDate(istMonthStart());
      setEndDate(isoToday());
    }
    else if (preset === '30d') { setStartDate(isoDaysAgo(29)); setEndDate(isoToday()); }
    // 'custom' → leave the two inputs where they are so the user can edit.
    else if (preset === 'custom' && !startDate && !endDate) {
      setStartDate(isoDaysAgo(29));
      setEndDate(isoToday());
    }
  };

  const fetchAnalytics = useCallback(() => {
    setLoading(true);
    setError(null);
    const params = {};
    if (startDate) params.start_date = startDate;
    if (endDate) params.end_date = endDate;
    api.getAdminAnalytics(params)
      .then(res => {
        setData(res.data);
        const m = res.data.monthly || [];
        // Default: latest = A (current month), previous = B
        if (m.length >= 2) {
          setMonthA(m[m.length - 1].month);
          setMonthB(m[m.length - 2].month);
        } else if (m.length === 1) {
          setMonthA(m[0].month);
          setMonthB(m[0].month);
        }
      })
      .catch(err => { setError(err.response?.data?.detail || 'Failed to load analytics'); })
      .finally(() => setLoading(false));
  }, [startDate, endDate]);

  useEffect(() => { fetchAnalytics(); }, [fetchAnalytics]);

  if (loading) {
    return (
      <div className="space-y-4" data-testid="analytics-loading">
        <div className="h-10 w-72 max-w-full rounded-full shimmer-bg animate-shimmer" />
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
          {[1, 2, 3, 4].map(i => <div key={i} className="h-28 rounded-2xl shimmer-bg animate-shimmer" />)}
        </div>
        <div className="h-72 rounded-2xl shimmer-bg animate-shimmer" />
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          <div className="h-64 rounded-2xl shimmer-bg animate-shimmer" />
          <div className="h-64 rounded-2xl shimmer-bg animate-shimmer" />
        </div>
      </div>
    );
  }

  if (error || !data) {
    return (
      <div className="heda-error my-10 text-center" data-testid="analytics-error">
        {error || 'No analytics data'}
      </div>
    );
  }

  const { summary, daily, monthly, by_salesperson, by_category, top_customers } = data;
  const topSalespeople = by_salesperson.slice(0, 10);

  // Build a quick {month: {count, value}} lookup so the compare card can
  // look up the two selected months in O(1) and compute deltas.
  const monthlyMap = {};
  monthly.forEach(m => { monthlyMap[m.month] = m; });
  const mA = monthlyMap[monthA] || { count: 0, value: 0 };
  const mB = monthlyMap[monthB] || { count: 0, value: 0 };
  const avgA = mA.count > 0 ? mA.value / mA.count : 0;
  const avgB = mB.count > 0 ? mB.value / mB.count : 0;
  const cmpCount = pctChange(mA.count, mB.count);
  const cmpValue = pctChange(mA.value, mB.value);
  const cmpAvg = pctChange(avgA, avgB);

  return (
    <div className="space-y-5" data-testid="analytics-dashboard">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-2">
        <div>
          <h2 className="font-display text-[19px] font-bold text-heda-ink">Analytics</h2>
          <p className="text-[13px] text-heda-sub">
            {data.date_range?.applied
              ? <>Showing <span className="font-semibold text-heda-ink">{data.date_range.start || '…'} → {data.date_range.end || '…'}</span></>
              : 'System-wide and per-salesperson performance (all-time)'}
          </p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          {/* Iter-127 — Comparison Analytics opens as its own full page for
              meetings-ready deep dives (period picker, pie charts, per-
              salesperson head-to-head, presentation mode). */}
          <button
            type="button"
            onClick={() => navigate('/admin/comparison-analytics')}
            className="heda-pill"
            data-testid="open-comparison-analytics-btn"
          >
            <GitCompareArrows className="w-3.5 h-3.5 text-heda-gold" />
            Comparison Analytics
          </button>
          <p className="text-[11.5px] text-heda-mute w-full sm:w-auto sm:ml-1">Across {summary.total_users} users · {summary.total_quotations} quotations{data.date_range?.applied ? ' in range' : ''}</p>
        </div>
      </div>

      {/* Iter-127 — Comparison Analytics moved to its own full-page route
          (`/admin/comparison-analytics`). Classic Analytics is preserved
          exactly as it was so owner can read the numbers at a glance
          without dealing with A/B period picker + delta chips. Deep
          dive lives on its own page for meetings. */}
      {/* Date range picker */}
      <DateRangeChips
        preset={rangePreset}
        startDate={startDate}
        endDate={endDate}
        onPreset={applyPreset}
        onStartDate={(v) => { setRangePreset('custom'); setStartDate(v); }}
        onEndDate={(v) => { setRangePreset('custom'); setEndDate(v); }}
      />

      {/* Stat cards — 4 across on desktop, 2 across on mobile */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        <StatCard
          icon={FileText}
          label="Total Quotations"
          value={summary.total_quotations.toLocaleString('en-IN')}
          sub={data.date_range?.applied ? 'in selected range' : `${summary.this_month_count} this month`}
          accent="text-heda-olive bg-heda-olive-tint/60 border-heda-olive/30"
        />
        <StatCard
          icon={IndianRupee}
          label="Total Value"
          value={short(summary.total_value)}
          sub={`Avg ${short(summary.avg_value)}`}
          accent="text-heda-gold bg-heda-gold/10 border-heda-gold/30"
        />
        <StatCard
          icon={Truck}
          label="Conversion Rate"
          value={`${summary.conversion_rate}%`}
          sub={`${summary.delivered_count} closed`}
          accent="text-emerald-700 bg-emerald-50 border-emerald-200"
        />
        <StatCard
          icon={TrendingUp}
          label="Avg Days to Close"
          value={summary.avg_days_to_close != null ? summary.avg_days_to_close.toString() : '—'}
          sub="closed in last 90 days"
          accent="text-heda-ink bg-white border-heda-line"
        />
      </div>

      {/* Order Status card — moved to top of analytics per owner priority.
          Closed / Pending / Lost + lost reasons is the KPI the admin looks
          at first, so it lives right below the stat cards. */}
      <OrderStatusOverviewCard data={data} />

      {/* Daily line chart — last 30 days */}
      <Card className="rounded-2xl border-heda-line bg-white shadow-none" data-testid="chart-daily">
        <CardHeader className="border-b border-heda-line px-5 py-3">
          <CardTitle className="flex items-center gap-2 font-display text-[15px] font-bold text-heda-ink">
            <TrendingUp className="w-4 h-4 text-heda-gold" /> Last 30 Days — Quotations &amp; Value
          </CardTitle>
        </CardHeader>
        <CardContent className="px-5 py-4">
          <div className="h-64 sm:h-72">
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={daily} margin={{ top: 8, right: 16, left: 0, bottom: 0 }}>
                <CartesianGrid {...GRID} />
                <XAxis dataKey="date" tickFormatter={dayLabel} tick={AXIS_TICK} stroke={C.line} interval="preserveStartEnd" minTickGap={16} />
                <YAxis yAxisId="left" tick={AXIS_TICK} stroke={C.line} allowDecimals={false} />
                <YAxis yAxisId="right" orientation="right" tickFormatter={short} tick={AXIS_TICK} stroke={C.line} />
                <Tooltip
                  formatter={(val, name) => name === 'value' ? short(val) : val}
                  labelFormatter={dayLabel}
                  contentStyle={TOOLTIP_STYLE}
                />
                <Legend wrapperStyle={{ fontSize: '11px' }} />
                <Line yAxisId="left" type="monotone" dataKey="count" name="Quotations" stroke={C.ink} strokeWidth={2} dot={{ r: 2 }} />
                <Line yAxisId="right" type="monotone" dataKey="value" name="Value" stroke={C.gold} strokeWidth={2} dot={{ r: 2 }} />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </CardContent>
      </Card>

      {/* Month-vs-month comparison card */}
      <Card className="rounded-2xl border-heda-line bg-white shadow-none" data-testid="card-month-compare">
        <CardHeader className="border-b border-heda-line px-5 py-3">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
            <CardTitle className="flex items-center gap-2 font-display text-[15px] font-bold text-heda-ink">
              <GitCompareArrows className="w-4 h-4 text-heda-gold" /> Compare Two Months
            </CardTitle>
            <div className="flex items-center gap-2 text-xs flex-wrap sm:flex-nowrap">
              <Select value={monthA} onValueChange={setMonthA}>
                <SelectTrigger className="h-9 w-32 rounded-xl border-heda-line bg-white text-xs" data-testid="month-a-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {monthly.slice().reverse().map(m => (
                    <SelectItem key={`A-${m.month}`} value={m.month}>{monthFullLabel(m.month)}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <span className="text-heda-mute">vs</span>
              <Select value={monthB} onValueChange={setMonthB}>
                <SelectTrigger className="h-9 w-32 rounded-xl border-heda-line bg-white text-xs" data-testid="month-b-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {monthly.slice().reverse().map(m => (
                    <SelectItem key={`B-${m.month}`} value={m.month}>{monthFullLabel(m.month)}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
        </CardHeader>
        <CardContent className="px-5 py-4">
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            <CompareTile
              label="Quotations"
              valueA={mA.count.toLocaleString('en-IN')}
              valueB={mB.count.toLocaleString('en-IN')}
              cmp={cmpCount}
              testId="cmp-quotations"
            />
            <CompareTile
              label="Total Value"
              valueA={short(mA.value)}
              valueB={short(mB.value)}
              cmp={cmpValue}
              testId="cmp-value"
            />
            <CompareTile
              label="Avg / Quote"
              valueA={short(avgA)}
              valueB={short(avgB)}
              cmp={cmpAvg}
              testId="cmp-avg"
            />
          </div>
          <p className="text-[11.5px] text-heda-mute text-center mt-3">
            <span className="font-semibold text-heda-ink">{monthFullLabel(monthA)}</span> compared against <span className="font-semibold text-heda-ink">{monthFullLabel(monthB)}</span>
          </p>
        </CardContent>
      </Card>

      {/* Two-column row — monthly bar + category donut */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <Card className="rounded-2xl border-heda-line bg-white shadow-none" data-testid="chart-monthly">
          <CardHeader className="border-b border-heda-line px-5 py-3">
            <CardTitle className="flex items-center gap-2 font-display text-[15px] font-bold text-heda-ink"><Calendar className="w-4 h-4 text-heda-gold" /> Monthly Trend (12m)</CardTitle>
          </CardHeader>
          <CardContent className="px-5 py-4">
            <div className="h-60">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={monthly} margin={{ top: 8, right: 16, left: 0, bottom: 0 }}>
                  <CartesianGrid {...GRID} />
                  <XAxis dataKey="month" tickFormatter={monthLabel} tick={AXIS_TICK} stroke={C.line} />
                  <YAxis tick={AXIS_TICK} stroke={C.line} allowDecimals={false} />
                  <Tooltip
                    labelFormatter={monthLabel}
                    formatter={(val, name) => name === 'value' ? short(val) : val}
                    contentStyle={TOOLTIP_STYLE}
                    cursor={{ fill: C.line, opacity: 0.4 }}
                  />
                  <Bar dataKey="count" name="Quotations" fill={C.gold} radius={[6, 6, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </CardContent>
        </Card>

        <Card className="rounded-2xl border-heda-line bg-white shadow-none" data-testid="chart-category">
          <CardHeader className="border-b border-heda-line px-5 py-3">
            <CardTitle className="flex items-center gap-2 font-display text-[15px] font-bold text-heda-ink">
              <Boxes className="w-4 h-4 text-heda-gold" /> Value Share by Category
              <span className="text-[11.5px] font-normal text-heda-mute ml-1">(hover / tap a slice)</span>
            </CardTitle>
          </CardHeader>
          <CardContent className="px-5 py-4">
            <AnimatedCategoryPie data={by_category} testId="classic-category-pie" />
          </CardContent>
        </Card>
      </div>

      {/* Top salespeople — horizontal bar */}
      <Card className="rounded-2xl border-heda-line bg-white shadow-none" data-testid="chart-salespeople">
        <CardHeader className="border-b border-heda-line px-5 py-3">
          <CardTitle className="flex items-center gap-2 font-display text-[15px] font-bold text-heda-ink">
            <Crown className="w-4 h-4 text-heda-gold" /> Top Salespeople by Value
          </CardTitle>
        </CardHeader>
        <CardContent className="px-5 py-4">
          {topSalespeople.length === 0 ? (
            <p className="text-sm text-heda-mute py-8 text-center">No data yet — quotations will populate this once created.</p>
          ) : (
            <div className="h-72">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={topSalespeople} layout="vertical" margin={{ top: 4, right: 60, left: 4, bottom: 0 }}>
                  <CartesianGrid {...GRID} vertical horizontal={false} />
                  <XAxis type="number" tickFormatter={short} tick={AXIS_TICK} stroke={C.line} />
                  <YAxis type="category" dataKey="name" tick={{ fontSize: 11, fill: C.ink }} stroke={C.line} width={96} />
                  <Tooltip
                    formatter={(val, name) => name === 'value' ? short(val) : val}
                    contentStyle={TOOLTIP_STYLE}
                    cursor={{ fill: C.line, opacity: 0.4 }}
                  />
                  <Bar dataKey="value" name="Value" fill={C.olive} radius={[0, 6, 6, 0]} label={{ position: 'right', formatter: short, fontSize: 10, fill: C.sub }} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Salesperson detail table — full breakdown with click-to-expand
          drill-down. Clicking a row reveals the last 5 quotations by that
          salesperson (scoped to the current date-range filter) plus a
          "View all" link that jumps to Quotation History pre-filtered. */}
      <SalespersonBreakdownTable
        rows={by_salesperson}
        startDate={startDate}
        endDate={endDate}
      />

      {/* Top customers */}
      <Card className="rounded-2xl border-heda-line bg-white shadow-none" data-testid="table-customers">
        <CardHeader className="border-b border-heda-line px-5 py-3">
          <CardTitle className="flex items-center gap-2 font-display text-[15px] font-bold text-heda-ink"><Crown className="w-4 h-4 text-heda-gold" /> Top 10 Customers</CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <table className="w-full text-left text-sm">
              <thead className="heda-thead">
                <tr>
                  <th className="px-3 sm:px-4 py-2.5 font-semibold hidden sm:table-cell">#</th>
                  <th className="px-3 sm:px-4 py-2.5 font-semibold">Customer</th>
                  <th className="px-3 sm:px-4 py-2.5 font-semibold hidden sm:table-cell">Mobile</th>
                  <th className="px-3 sm:px-4 py-2.5 font-semibold text-right">Quotations</th>
                  <th className="px-3 sm:px-4 py-2.5 font-semibold text-right">Total Value</th>
                </tr>
              </thead>
              <tbody>
                {top_customers.length === 0 ? (
                  <tr><td colSpan="5" className="px-5 py-10 text-center text-sm text-heda-mute">No customers yet</td></tr>
                ) : top_customers.map((c, i) => (
                  <tr key={`${c.mobile}-${i}`} className="border-t border-heda-line hover:bg-heda-cream/50">
                    <td className="px-3 sm:px-4 py-3 text-heda-mute hidden sm:table-cell">{i + 1}</td>
                    <td className="px-3 sm:px-4 py-3">
                      <div className="font-medium text-heda-ink">{c.name}</div>
                      <div className="text-[11px] text-heda-mute tabular-nums sm:hidden">{c.mobile || '—'}</div>
                    </td>
                    <td className="px-3 sm:px-4 py-3 tabular-nums text-heda-sub hidden sm:table-cell">{c.mobile || '—'}</td>
                    <td className="px-3 sm:px-4 py-3 text-right tabular-nums text-heda-ink/80">{c.count}</td>
                    <td className="px-3 sm:px-4 py-3 text-right tabular-nums font-bold text-heda-ink">{formatINR(c.value)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>

      {/* Month-vs-Month kept as legacy widget below the main comparison view.
          Referrer (architects/carpenters) and PurchaseOrder analytics kept
          in the classic analytics view — they were only excluded from the
          new comparison view per iter-124 owner directive. */}
      <MonthCompareCard data={data} />
      <ReferrerAnalyticsCard />
      <PurchaseOrderAnalyticsCard />
    </div>
  );
}

// =====================================================================
// Order Status Overview — Closed / Pending / Lost summary, per-staff
// breakdown, and lost-reasons table. Data comes from the existing
// /admin/analytics payload (status_breakdown, by_salesperson with
// closed_count/pending_count/lost_count, lost_reasons).
// =====================================================================
function OrderStatusOverviewCard({ data }) {
  const [selectedStaff, setSelectedStaff] = useState(null);
  const [themes, setThemes] = useState('');
  const [themesLoading, setThemesLoading] = useState(false);
  const lostReasons = data?.lost_reasons || [];

  // Fetch the AI-clustered themes once there are enough lost reasons to
  // make clustering worthwhile (3+). Falls back to raw list when LLM
  // unavailable — handled server-side.
  useEffect(() => {
    if (!data || lostReasons.length < 3) { setThemes(''); return; }
    setThemesLoading(true);
    api.getLostReasonsSummary()
      .then(r => setThemes(r.data?.themes || ''))
      .catch(() => setThemes(''))
      .finally(() => setThemesLoading(false));
  }, [data, lostReasons.length]);

  if (!data) return null;
  const sb = data.status_breakdown || { counts: { closed: 0, pending: 0, lost: 0 }, values: { closed: 0, pending: 0, lost: 0 }, total: 0 };
  const staffRows = (data.by_salesperson || []).filter(r => (r.closed_count || 0) + (r.pending_count || 0) + (r.lost_count || 0) > 0);

  const total = Math.max(1, sb.total);
  const pieData = [
    { name: 'Closed', value: sb.counts.closed, color: C.emerald },
    { name: 'Pending', value: sb.counts.pending, color: C.amber },
    { name: 'Lost', value: sb.counts.lost, color: C.red },
  ];

  const closedPct = ((sb.counts.closed / total) * 100).toFixed(0);
  const lostPct = ((sb.counts.lost / total) * 100).toFixed(0);

  return (
    <Card className="rounded-2xl border-heda-line bg-white shadow-none" data-testid="status-overview-card">
      <CardHeader className="border-b border-heda-line px-5 py-3">
        <CardTitle className="flex items-center gap-2 font-display text-[15px] font-bold text-heda-ink">
          <TrendingUp className="w-4 h-4 text-heda-gold" /> Order Status — Closed / Pending / Lost
        </CardTitle>
        <p className="text-[11.5px] text-heda-mute" data-testid="status-overview-summary">
          {sb.total} quotations · {closedPct}% closed · {lostPct}% lost
        </p>
      </CardHeader>
      <CardContent className="px-5 py-4">
        <div className="grid grid-cols-3 gap-2 sm:gap-3 mb-4">
          <div className="rounded-xl border border-emerald-200 bg-emerald-50/60 p-3 sm:p-4" data-testid="status-card-closed">
            <p className="text-[10.5px] font-semibold uppercase tracking-wider text-emerald-700">Closed</p>
            <p className="mt-1 font-display text-2xl font-bold tabular-nums text-heda-ink">{sb.counts.closed}</p>
            <p className="text-[11px] text-heda-mute tabular-nums truncate">{formatINR(sb.values.closed)}</p>
          </div>
          <div className="rounded-xl border border-amber-200 bg-amber-50/60 p-3 sm:p-4" data-testid="status-card-pending">
            <p className="text-[10.5px] font-semibold uppercase tracking-wider text-amber-700">Pending</p>
            <p className="mt-1 font-display text-2xl font-bold tabular-nums text-heda-ink">{sb.counts.pending}</p>
            <p className="text-[11px] text-heda-mute tabular-nums truncate">{formatINR(sb.values.pending)}</p>
          </div>
          <div className="rounded-xl border border-red-200 bg-red-50/60 p-3 sm:p-4" data-testid="status-card-lost">
            <p className="text-[10.5px] font-semibold uppercase tracking-wider text-red-700">Lost</p>
            <p className="mt-1 font-display text-2xl font-bold tabular-nums text-heda-ink">{sb.counts.lost}</p>
            <p className="text-[11px] text-heda-mute tabular-nums truncate">{formatINR(sb.values.lost)}</p>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          {/* Donut chart */}
          <div className="h-48">
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <Pie data={pieData} dataKey="value" nameKey="name" innerRadius={45} outerRadius={75} paddingAngle={2}>
                  {pieData.map((d) => <Cell key={d.name} fill={d.color} />)}
                </Pie>
                <Tooltip contentStyle={TOOLTIP_STYLE} />
                <Legend wrapperStyle={{ fontSize: '12px' }} />
              </PieChart>
            </ResponsiveContainer>
          </div>

          {/* Per-staff table */}
          <div className="overflow-auto max-h-60 rounded-xl border border-heda-line">
            <table className="w-full text-left text-sm">
              <thead className="heda-thead sticky top-0">
                <tr>
                  <th className="px-3 py-2.5 font-semibold">Staff</th>
                  <th className="px-3 py-2.5 font-semibold text-right text-emerald-700">Closed</th>
                  <th className="px-3 py-2.5 font-semibold text-right text-amber-700">Pending</th>
                  <th className="px-3 py-2.5 font-semibold text-right text-red-700">Lost</th>
                </tr>
              </thead>
              <tbody>
                {staffRows.length === 0 ? (
                  <tr><td colSpan="4" className="px-4 py-8 text-center text-heda-mute">No staff status data yet.</td></tr>
                ) : staffRows.map(r => (
                  <tr
                    key={r.user_id}
                    onClick={() => setSelectedStaff(selectedStaff === r.user_id ? null : r.user_id)}
                    className={`border-t border-heda-line cursor-pointer hover:bg-heda-cream/50 ${selectedStaff === r.user_id ? 'bg-heda-gold-tint/40' : ''}`}
                    data-testid={`status-staff-row-${r.user_id}`}
                  >
                    <td className="px-3 py-2.5 font-medium text-heda-ink">{r.name || 'Unknown'}</td>
                    <td className="px-3 py-2.5 text-right tabular-nums">{r.closed_count}</td>
                    <td className="px-3 py-2.5 text-right tabular-nums">{r.pending_count}</td>
                    <td className="px-3 py-2.5 text-right tabular-nums">{r.lost_count}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        {/* Lost reasons table — appears when there are any */}
        {lostReasons.length > 0 && (
          <div className="mt-4">
            {/* AI-clustered themes — the LLM groups variants like
                "price too high" / "very expensive" / "cost issue" into
                top 2-3 themes. Hidden until at least 3 reasons exist. */}
            {(themes || themesLoading) && (
              <div className="mb-3 rounded-xl border border-heda-gold/30 bg-heda-gold-tint/50 p-3" data-testid="lost-themes-summary">
                <p className="mb-1 text-[10.5px] font-semibold uppercase tracking-wider text-heda-gold">
                  Top themes ({themesLoading ? 'AI summarising…' : 'AI clustered'})
                </p>
                <p className="text-xs text-heda-sub whitespace-pre-line">{themes || 'Working on it…'}</p>
              </div>
            )}
            <p className="heda-label !text-red-700 mb-2">
              Lost Reasons {selectedStaff ? `(filtered to selected staff)` : `(${lostReasons.length})`}
            </p>
            <div className="overflow-auto max-h-64 rounded-xl border border-heda-line" data-testid="lost-reasons-table">
              <table className="w-full text-left text-sm">
                <thead className="heda-thead sticky top-0">
                  <tr>
                    <th className="px-3 py-2.5 font-semibold hidden sm:table-cell">Staff</th>
                    <th className="px-3 py-2.5 font-semibold">Customer</th>
                    <th className="px-3 py-2.5 font-semibold">Reason</th>
                    <th className="px-3 py-2.5 font-semibold text-right">Value</th>
                  </tr>
                </thead>
                <tbody>
                  {lostReasons
                    .filter(r => !selectedStaff || (data.by_salesperson || []).find(s => s.user_id === selectedStaff)?.name === r.staff_name)
                    .map((r, i) => (
                    <tr key={i} className="border-t border-heda-line hover:bg-heda-cream/50">
                      <td className="px-3 py-2.5 text-heda-ink hidden sm:table-cell">{r.staff_name}</td>
                      <td className="px-3 py-2.5 text-heda-ink">
                        {r.customer_name}
                        {r.customer_mobile && <div className="text-[11px] text-heda-mute tabular-nums">{r.customer_mobile}</div>}
                      </td>
                      <td className="px-3 py-2.5 text-heda-sub italic">&ldquo;{r.reason}&rdquo;</td>
                      <td className="px-3 py-2.5 text-right tabular-nums text-red-700 whitespace-nowrap">{formatINR(r.value)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            {selectedStaff && (
              <p className="text-[11px] text-heda-mute mt-1">Click staff row again to clear filter.</p>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
}

// =====================================================================
// Day-by-day This Month vs Last Month line chart
// =====================================================================
function MonthCompareCard({ data }) {
  if (!data?.month_compare) return null;
  const series = data.month_compare;
  const s = data.month_compare_summary || {};
  return (
    <Card className="rounded-2xl border-heda-line bg-white shadow-none" data-testid="month-compare-card">
      <CardHeader className="border-b border-heda-line px-5 py-3">
        <CardTitle className="flex items-center gap-2 font-display text-[15px] font-bold text-heda-ink">
          <Calendar className="w-4 h-4 text-heda-gold" /> This Month vs Last Month — Day by Day
        </CardTitle>
        <p className="text-[11.5px] text-heda-mute" data-testid="month-compare-summary">
          <span className="text-heda-ink font-semibold">{s.this_label}</span>: {s.this_total_count} quotes · {formatINR(s.this_total_value)} ·{' '}
          <span className="text-heda-gold font-semibold">{s.last_label}</span>: {s.last_total_count} quotes · {formatINR(s.last_total_value)}
        </p>
      </CardHeader>
      <CardContent className="px-5 py-4">
        <div className="h-56">
          <ResponsiveContainer width="100%" height="100%">
            <LineChart data={series} margin={{ top: 8, right: 16, left: 0, bottom: 0 }}>
              <CartesianGrid {...GRID} />
              <XAxis dataKey="day" tick={AXIS_TICK} stroke={C.line} interval="preserveStartEnd" minTickGap={8} />
              <YAxis yAxisId="left" tick={AXIS_TICK} stroke={C.line} />
              <YAxis yAxisId="right" orientation="right" tick={AXIS_TICK} stroke={C.line} tickFormatter={short} />
              <Tooltip formatter={(v, n) => n.includes('value') ? formatINR(v) : v} contentStyle={TOOLTIP_STYLE} />
              <Legend wrapperStyle={{ fontSize: '12px' }} />
              <Line yAxisId="left" type="monotone" dataKey="this_count" name={`${s.this_label} count`} stroke={C.ink} strokeWidth={2} dot={{ r: 3 }} />
              <Line yAxisId="left" type="monotone" dataKey="last_count" name={`${s.last_label} count`} stroke={C.mute} strokeWidth={2} strokeDasharray="5 5" dot={{ r: 2 }} />
              <Line yAxisId="right" type="monotone" dataKey="this_value" name={`${s.this_label} value`} stroke={C.gold} strokeWidth={2} dot={{ r: 3 }} />
              <Line yAxisId="right" type="monotone" dataKey="last_value" name={`${s.last_label} value`} stroke={C.olive} strokeWidth={2} strokeDasharray="5 5" dot={{ r: 2 }} />
            </LineChart>
          </ResponsiveContainer>
        </div>
      </CardContent>
    </Card>
  );
}

// =====================================================================
// Referrer Analytics (Carpenters & Architects)
// Optional fields captured on each quotation; admins use this to see
// which referrers are driving business and which customers came in
// through them.
// =====================================================================
function ReferrerAnalyticsCard() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [search, setSearch] = useState('');
  const [expanded, setExpanded] = useState({ carpenter: null, architect: null });

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError('');
    api.getReferrerAnalytics({ search })
      .then(res => { if (!cancelled) setData(res.data); })
      .catch(err => { if (!cancelled) setError(err.response?.data?.detail || 'Failed to load referrer analytics'); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [search]);

  const renderList = (kind, items) => (
    <div className="space-y-1">
      {items.length === 0 ? (
        <p className="text-xs text-heda-mute py-4 text-center">
          No {kind === 'carpenter' ? 'carpenters' : 'architects'} {search ? `match "${search}"` : 'yet'}.
          {!search && ` Add ${kind === 'carpenter' ? 'a carpenter' : 'an architect'} name on a quotation to start tracking.`}
        </p>
      ) : items.map((r, i) => {
        const isOpen = expanded[kind] === r.name;
        return (
          <div key={r.name} className="rounded-xl border border-heda-line overflow-hidden">
            <button
              type="button"
              onClick={() => setExpanded(e => ({ ...e, [kind]: isOpen ? null : r.name }))}
              className="w-full flex items-center justify-between gap-2 px-3 py-2.5 text-left text-sm hover:bg-heda-cream/50"
              data-testid={`${kind}-row-${i}`}
            >
              <div className="flex items-center gap-2 min-w-0">
                <span className="text-heda-mute font-mono w-5 text-right text-xs">{i + 1}</span>
                <span className="font-medium text-heda-ink truncate">{r.name}</span>
                <span className="shrink-0 rounded-full bg-heda-cream px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider text-heda-mute">{r.count} {r.count === 1 ? 'quote' : 'quotes'}</span>
              </div>
              <span className="tabular-nums text-heda-ink font-bold text-sm whitespace-nowrap">{formatINR(r.value)}</span>
            </button>
            {isOpen && (
              <div className="px-3 py-2 bg-heda-cream/50 border-t border-heda-line text-xs">
                <p className="mb-1 text-[10.5px] font-semibold uppercase tracking-wider text-heda-mute">Customers ({r.customers.length})</p>
                <div className="space-y-0.5">
                  {r.customers.map((c, ci) => (
                    <div key={ci} className="flex items-center justify-between">
                      <span className="truncate text-heda-ink">{c.name}{c.mobile ? ` · ${c.mobile}` : ''}</span>
                      <span className="tabular-nums text-heda-sub whitespace-nowrap ml-2">{c.count}× · {formatINR(c.value)}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        );
      })}
    </div>
  );

  return (
    <Card className="rounded-2xl border-heda-line bg-white shadow-none" data-testid="referrer-analytics-card">
      <CardHeader className="border-b border-heda-line px-5 py-3">
        <div className="flex items-center justify-between gap-3 flex-wrap">
          <CardTitle className="flex items-center gap-2 font-display text-[15px] font-bold text-heda-ink">
            <Users className="w-4 h-4 text-heda-gold" /> Carpenter & Architect Referrers
          </CardTitle>
          <input
            type="search"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search referrer name…"
            className="heda-input !py-2 text-base sm:!text-sm w-full sm:w-56"
            data-testid="referrer-search-input"
          />
        </div>
        {data?.totals && (
          <p className="text-[11.5px] text-heda-mute">
            {data.totals.carpenter_count} carpenters · {data.totals.architect_count} architects · referred {formatINR((data.totals.carpenter_value || 0) + (data.totals.architect_value || 0))} in business
          </p>
        )}
      </CardHeader>
      <CardContent className="px-5 py-4">
        {loading && <Skeleton className="h-24 w-full rounded-xl" />}
        {!loading && error && <p className="heda-error">{error}</p>}
        {!loading && !error && data && (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            <div>
              <p className="heda-label mb-2">Carpenters</p>
              {/* Scroll-cap: keeps the dashboard from stretching when
                  many referrers have been logged. Internal scroll only. */}
              <div className="max-h-80 overflow-y-auto pr-1" data-testid="carpenter-list-scroll">
                {renderList('carpenter', data.carpenters || [])}
              </div>
            </div>
            <div>
              <p className="heda-label mb-2">Architects</p>
              <div className="max-h-80 overflow-y-auto pr-1" data-testid="architect-list-scroll">
                {renderList('architect', data.architects || [])}
              </div>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

// =====================================================================
// Purchase Order Analytics
// Total POs, total value, total items ordered + tables by supplier,
// brand, and product.
// =====================================================================
function PurchaseOrderAnalyticsCard() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [tab, setTab] = useState('supplier');

  useEffect(() => {
    let cancelled = false;
    api.getPOAnalytics()
      .then(res => { if (!cancelled) setData(res.data); })
      .catch(err => { if (!cancelled) setError(err.response?.data?.detail || 'Failed to load PO analytics'); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, []);

  const TABS = [
    { value: 'supplier', label: 'By Supplier', key: 'by_supplier' },
    { value: 'brand', label: 'By Brand', key: 'by_brand' },
    { value: 'product', label: 'By Product', key: 'by_product' },
  ];

  const activeKey = TABS.find(t => t.value === tab)?.key || 'by_supplier';
  const rows = data?.[activeKey] || [];

  return (
    <Card className="rounded-2xl border-heda-line bg-white shadow-none" data-testid="po-analytics-card">
      <CardHeader className="border-b border-heda-line px-5 py-3">
        <CardTitle className="flex items-center gap-2 font-display text-[15px] font-bold text-heda-ink">
          <Boxes className="w-4 h-4 text-heda-gold" /> Purchase Order Analytics
        </CardTitle>
        {data?.summary && (
          <p className="text-[11.5px] text-heda-mute" data-testid="po-summary-line">
            {data.summary.po_count} POs · {data.summary.total_items_qty.toLocaleString('en-IN')} items ordered · {formatINR(data.summary.total_value)}
          </p>
        )}
      </CardHeader>
      <CardContent className="px-5 py-4">
        {loading && <Skeleton className="h-24 w-full rounded-xl" />}
        {!loading && error && <p className="heda-error">{error}</p>}
        {!loading && !error && data && (
          <>
            <div className="heda-seg mb-3 w-fit max-w-full overflow-x-auto" role="tablist">
              {TABS.map(t => (
                <button
                  key={t.value}
                  type="button"
                  onClick={() => setTab(t.value)}
                  className={`shrink-0 whitespace-nowrap ${tab === t.value ? 'is-on' : 'hover:text-heda-ink'}`}
                  data-testid={`po-tab-${t.value}`}
                >
                  {t.label}
                </button>
              ))}
            </div>
            <div className="overflow-x-auto rounded-xl border border-heda-line">
              <table className="w-full text-left text-sm">
                <thead className="heda-thead">
                  <tr>
                    <th className="px-3 sm:px-4 py-2.5 font-semibold hidden sm:table-cell">#</th>
                    <th className="px-3 sm:px-4 py-2.5 font-semibold">{tab === 'supplier' ? 'Supplier' : tab === 'brand' ? 'Brand' : 'Product'}</th>
                    <th className="px-3 sm:px-4 py-2.5 font-semibold text-right">POs</th>
                    <th className="px-3 sm:px-4 py-2.5 font-semibold text-right hidden sm:table-cell">Qty</th>
                    <th className="px-3 sm:px-4 py-2.5 font-semibold text-right">Value</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.length === 0 ? (
                    <tr><td colSpan="5" className="px-4 py-8 text-center text-heda-mute">No purchase orders yet.</td></tr>
                  ) : rows.slice(0, 20).map((r, i) => (
                    <tr key={i} className="border-t border-heda-line hover:bg-heda-cream/50">
                      <td className="px-3 sm:px-4 py-2.5 text-heda-mute hidden sm:table-cell">{i + 1}</td>
                      <td className="px-3 sm:px-4 py-2.5 font-medium text-heda-ink">
                        {tab === 'supplier' ? r.supplier_name : tab === 'brand' ? r.brand_name : (<><span>{r.brand_name}</span> <span className="text-heda-mute">· {r.name}</span></>)}
                      </td>
                      <td className="px-3 sm:px-4 py-2.5 text-right tabular-nums">{r.po_count}</td>
                      <td className="px-3 sm:px-4 py-2.5 text-right tabular-nums hidden sm:table-cell">{Number(r.total_qty || 0).toLocaleString('en-IN')}</td>
                      <td className="px-3 sm:px-4 py-2.5 text-right tabular-nums font-bold text-heda-ink whitespace-nowrap">{formatINR(r.total_value)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Daily PO line chart — last 30 days */}
            {data.daily && data.daily.length > 0 && (
              <div className="mt-4 h-44">
                <p className="heda-label">Last 30 days — PO count</p>
                <ResponsiveContainer width="100%" height="100%">
                  <LineChart data={data.daily} margin={{ top: 8, right: 16, left: 0, bottom: 0 }}>
                    <CartesianGrid {...GRID} />
                    <XAxis dataKey="date" tickFormatter={dayLabel} tick={AXIS_TICK} stroke={C.line} interval="preserveStartEnd" minTickGap={16} />
                    <YAxis tick={AXIS_TICK} stroke={C.line} allowDecimals={false} />
                    <Tooltip contentStyle={TOOLTIP_STYLE} />
                    <Line type="monotone" dataKey="count" name="POs" stroke={C.ink} strokeWidth={2} dot={{ r: 2 }} />
                  </LineChart>
                </ResponsiveContainer>
              </div>
            )}

            {/* Recent POs — flat table with date + PO number so admins can
                cross-check against the stock register. Limited to last 20
                for the dashboard view (full list is on the POs page). */}
            {data.recent_pos && data.recent_pos.length > 0 && (
              <div className="mt-4">
                <p className="heda-label mb-2">Recent Purchase Orders</p>
                <div className="overflow-auto max-h-64 rounded-xl border border-heda-line" data-testid="recent-pos-table">
                  <table className="w-full text-left text-sm">
                    <thead className="heda-thead sticky top-0">
                      <tr>
                        <th className="px-3 sm:px-4 py-2.5 font-semibold">PO #</th>
                        <th className="px-3 sm:px-4 py-2.5 font-semibold">Supplier</th>
                        <th className="px-3 sm:px-4 py-2.5 font-semibold hidden md:table-cell">Date</th>
                        <th className="px-3 sm:px-4 py-2.5 font-semibold text-right hidden sm:table-cell">Items</th>
                        <th className="px-3 sm:px-4 py-2.5 font-semibold text-right hidden sm:table-cell">Qty</th>
                        <th className="px-3 sm:px-4 py-2.5 font-semibold">Status</th>
                      </tr>
                    </thead>
                    <tbody>
                      {data.recent_pos.slice(0, 20).map(p => (
                        <tr key={p.id} className="border-t border-heda-line hover:bg-heda-cream/50">
                          <td className="px-3 sm:px-4 py-2.5 font-mono text-xs text-heda-ink">{p.po_number || p.id.slice(0, 8)}</td>
                          <td className="px-3 sm:px-4 py-2.5 text-heda-ink">{p.supplier_name || '—'}</td>
                          <td className="px-3 sm:px-4 py-2.5 text-heda-sub hidden md:table-cell">{p.po_date || (p.created_at ? new Date(p.created_at).toLocaleDateString('en-IN') : '—')}</td>
                          <td className="px-3 sm:px-4 py-2.5 text-right tabular-nums hidden sm:table-cell">{p.items_count}</td>
                          <td className="px-3 sm:px-4 py-2.5 text-right tabular-nums hidden sm:table-cell">{Number(p.items_qty || 0).toLocaleString('en-IN')}</td>
                          <td className="px-3 sm:px-4 py-2.5"><span className="rounded-full bg-heda-cream px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider text-heda-mute">{p.status || '—'}</span></td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </>
        )}
      </CardContent>
    </Card>
  );
}
